import React, { useEffect, useRef, useState } from 'react';
import { Segment, Container, Header, Image, Icon, Button } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { HtmlEditor, TableContextMenu, TableResizing } from 'devextreme-react/html-editor';
import McqsQuestionTemplate from '../Templates/McqsQuestionTemplate';
import TextQuestionTemplate from '../Templates/TextQuestionTemplate';
import McqsQuestionMockCMATemplate from '../Templates/McqsQuestionMockCMATemplate';
import { Calculator, ErrorSegment, FlexWindow } from '../../..';
import { Consts, Styles } from '../../../../res';
import iconImage from '../../../../res/images/logo.svg';
import { applyHighlights, deleteHighlights, getSelectedText, replaceFontFamily } from '../../../../helpers/general';

const { noExtraSpaces } = Styles;

const QuestionPage = (props) => {

    const { Q, onAnswerQuestion, loading, error, isMockExam, isViewOnly, user, onHideSomeAnswers, onExcludeOption, userExamHighlightText } = props;
    const { t } = useTranslation();
    const pageRef = useRef();
    const [showScrollbar, setShowScrollbar] = useState(false);
    const [showCalculator, setShowCalculator] = useState(false);
    const [showFact, setShowFact] = useState(false);
    const [selectedHighlight, setSelectedHighlight] = useState(null);

    useEffect(() => {
        setShowFact(false);
        setSelectedHighlight(null);
    }, [Q]);

    useEffect(() => {
        setTimeout(() => applyHighlights(pageRef, Q?.highlights || [], setSelectedHighlight), 0);
    }, [Q]);

    useEffect(() => {
        const scrolableElements = document.getElementsByClassName('question-body');
        const scrolableElement = scrolableElements.length > 0 ? scrolableElements[0] : null;

        const checkForScrollbar = () => {
            if (scrolableElement && scrolableElement.scrollHeight > scrolableElement.clientHeight) {
                setShowScrollbar(true);
            }
        };
        const handleScroll = () => {
            (scrolableElement && scrolableElement.scrollTop > 0) && setShowScrollbar(false);
        };
        checkForScrollbar();
        if (scrolableElement)
            scrolableElement.addEventListener('scroll', handleScroll);

        return () => {
            setShowScrollbar(false);
            if (scrolableElement)
                scrolableElement.removeEventListener('scroll', handleScroll);
        };
    }, [Q?.questionId, loading]);

    if (loading || !Q) return <Segment basic style={{ height: '40vh', ...noExtraSpaces }} loading={loading} />;
    if (error) return <ErrorSegment />;

    const renderMcqsTemplate = () => {
        if (Q.questionTypeId === Consts.QUESTIONS_TYPES.MCQS) {
            if (isMockExam)
                return <McqsQuestionMockCMATemplate Q={Q} onAnswerQuestion={onAnswerQuestion} isViewOnly={isViewOnly} onExcludeOption={onExcludeOption} />;
            else return <McqsQuestionTemplate Q={Q} onAnswerQuestion={onAnswerQuestion} isViewOnly={isViewOnly} onHideSomeAnswers={onHideSomeAnswers} onExcludeOption={onExcludeOption} />;
        }
        return null;
    };

    const isEssay = Q?.questionTypeId === Consts.QUESTIONS_TYPES.ESSAY;

    return (
        <div ref={pageRef}
            onMouseDown={() => selectedHighlight && setSelectedHighlight(null)}
            onMouseUp={() => {
                selectedHighlight?.selection?.removeAllRanges();
                const selectionData = getSelectedText(pageRef);
                if (selectionData) {
                    setSelectedHighlight({ ...selectionData, type: "SET_HIGHLIGHT" });
                }
            }}
            className='tw-relative'
        >

            {showScrollbar && <div
                className='tw-text-xl tw-fixed tw-bg-SECONDARY_60 tw-bottom-10 tw-z-50 tw-right-5 tw-p-5 tw-text-white'>
                {t("PageRequiresScrolling")}
                <span className="tw-mx-3"><Icon link name='x' size='small' circular style={{ backgroundColor: 'white', color: 'black' }} onClick={() => setShowScrollbar(false)} /></span>
            </div>}

            <Segment basic className='question-body'>
                {
                    isEssay ? <div className={`tw-grid tw-gap-5 tw-mb-10 md:tw-mb-0 ${!Q?.showFactOnly && showFact && "md:tw-grid-cols-2"}`}>
                        <div>
                            {(showFact || Q?.showFactOnly) && <HtmlEditor value={replaceFontFamily(Q.factContent.trim())} readOnly style={{ backgroundColor: '#ECECEC', minHeight: '35vh', }} className='resizo' >
                                <TableContextMenu enabled />
                                <TableResizing enabled />
                            </HtmlEditor>}
                        </div>

                        {!Q?.showFactOnly && <div>
                            <HtmlEditor value={replaceFontFamily(Q.questionText.trim())} readOnly style={{ backgroundColor: '#ECECEC' }} className='resizo' >
                                <TableContextMenu enabled />
                                <TableResizing enabled />
                            </HtmlEditor>

                            <Segment basic>
                                <Button content={t("Calculator")} icon='calculator' onClick={() => setShowCalculator(true)} />
                                <Button content={showFact ? t("HideFact") : t("ShowFact")} onClick={() => setShowFact(prev => !prev)} />
                            </Segment>
                            <TextQuestionTemplate Q={Q} onAnswerQuestion={onAnswerQuestion} isMockExam={isMockExam} isViewOnly={isViewOnly} />
                        </div>}
                    </div>

                        : <div>
                            <HtmlEditor value={replaceFontFamily(Q.questionText.trim())} readOnly style={{ backgroundColor: '#ECECEC' }} className='resizo' >
                                <TableContextMenu enabled />
                                <TableResizing enabled />
                            </HtmlEditor>

                            <Segment basic>
                                <Button icon='calculator' onClick={() => setShowCalculator(true)} />
                            </Segment>
                            {renderMcqsTemplate()}
                        </div>
                }
            </Segment>

            <div className='studentExamWatermark' style={{ top: '40%', left: '40%' }}>
                <Image loading="lazy" size='medium' src={iconImage} />
            </div>

            <Header
                as='h1'
                content={`${user.firstName} ${user.lastName} [${user.email}] ${user.contactNumber}`}
                className='studentExamWatermark'
            />

            {showCalculator && <FlexWindow
                isVisible={showCalculator}
                onClose={() => setShowCalculator(false)}
                title={t("Calculator")}
            >
                <Calculator />
            </FlexWindow>}


            {selectedHighlight && <div
                style={{ position: "fixed", zIndex: 100, top: selectedHighlight?.data?.rect?.y + 30, left: selectedHighlight?.data?.rect?.x + 30 }}
                onMouseDown={(e) => {
                    e.stopPropagation();
                    if (selectedHighlight.type === "SET_HIGHLIGHT") {
                        selectedHighlight.selection.removeAllRanges();
                        userExamHighlightText({ ...Q, highlights: [...Q.highlights, selectedHighlight.data] });
                        setSelectedHighlight(null);
                    } else if (selectedHighlight.type === "REMOVE_HIGHLIGHT") {
                        const newHighlights = Q.highlights.filter(h => h.id !== selectedHighlight.data.id);
                        deleteHighlights(pageRef);
                        userExamHighlightText({ ...Q, highlights: newHighlights });
                        setSelectedHighlight(null);
                    }
                }}
            >
                <Icon link inverted bordered name='paint brush' color={selectedHighlight.type === "SET_HIGHLIGHT" ? 'green' : 'red'} />
            </div>}

        </div>
    );
};

export default QuestionPage;